import { t } from 'src/lib/language';
import {
  CSSProperties,
  useCallback,
  useState,
  useEffect,
} from 'react';
import { useSelector } from 'react-redux';
import { Store, useAppDispatch } from 'src/redux/Store';
import { debounce, FormControl, TextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { makeStyles } from '@material-ui/core/styles';
import { getAutoCompleteAction } from 'src/redux/actions/autoCompleteActions';
import { getContactByIdAction } from 'src/redux/actions/contactActions';
import { setSelections } from 'src/redux/actions/selectionActions';
import { openModal } from 'src/redux/actions/modalActions';
import { AUTOCOMPLETE_ID, AUTOCOMPLETE_TYPE } from 'src/constants/Autocomplete';
import { ContactItem, ContactOption, ContactOptions } from 'src/models/AutocompleteModel';
import { ENTITIES } from 'src/models/QuerybuilderModel';
import { MODALS } from 'src/models/ModalModel';
import { FORM_ID } from 'src/models/FormModel';

interface Props {
  autocompleteId: AUTOCOMPLETE_ID,
  contactId?: number,
  disabled?: boolean,
  error? : string;
  form?: FORM_ID,
  hideEdit?: boolean,
  hideAdd?: boolean,
  id?: string,
  label?: string,
  margin?: 'dense' | 'normal' | undefined,
  name?: string,
  onChange: (id:number) => void,
  style?: CSSProperties,
}

const useStyles = makeStyles(() => ({
  autocomplete: {
    minWidth: '600px',
  },
  flexrow: {
    display: 'flex',
    flexDirection: 'row',
    marginRight: '5px',
  },
  formControl: {
    minWidth: '200px',
    marginTop: '4px',
    width: '100%',
  },
  link: {
    color: '#0A4DF2',
    '&:hover': {
      textDecoration: 'underline',
      cursor: 'pointer',
    },
    marginBottom: 0,
  },
  link_disabled: {
    color: '#454444',
    marginBottom: 0,
  },
  root: {
    '& .MuiFilledInput-root': {
      background: 'rgb(255, 255, 255)',
    },
  },
}));

const enum STATE {
  render = 'render',
  search = 'search',
}

const ContactSelector = (props: Props) => {
  const {
    autocompleteId,
    contactId,
    disabled,
    error,
    form,
    hideEdit,
    hideAdd,
    id,
    label,
    margin,
    name,
    onChange,
    style,
  } = props;
  const dispatch = useAppDispatch();
  const classes = useStyles();
  const lang = useSelector((state: Store) => state.language.language ?? 'en');

  const contactString = useSelector((state: Store) => {
    if (!contactId) return '';
    if (!!state.contacts[contactId]) {
      const { contact } = state.contacts[contactId];
      if (!contact) return '';
      const address = contact.billing_address ? `(${contact.billing_address})` : '';
      if (!!contact.company_name) {
        return `${contact.company_name} ${address}`;
      }
      return `${contact.first_name} ${contact.last_name} (${contact.billing_address})`;
    }
    return '';
  });

  useEffect(() => {
    if (contactId && contactString === undefined) {
      // Only dispatch if contactId exists and contactString is undefined
      dispatch(getContactByIdAction(contactId));
    }
  }, [contactId, contactString, dispatch]); // Add contactString as a dependency

  const errors: string[] | null =
    useSelector((state:Store) => {
      if (form && name) {
        if (state.errors[form]) {
          return state.errors[form][name] as string[] || null;
        }
      }
      return null;
    });

  const [contactOptions, setContactOptions] = useState([] as ContactOptions);
  const [state, setState] = useState(STATE.render);

  const getContactOptions = async (search: string) => {
    const options = await dispatch(getAutoCompleteAction(
      autocompleteId,
      search,
      AUTOCOMPLETE_TYPE.contact,
    ));
    setContactOptions(options);
  };

  const searchContact = useCallback(
    debounce((search: string) => {
      getContactOptions(search);
    }, 300),
    [], // will be created only once initially
  );

  const onInputChange = (search: string) => {
    if (search.length < 3) { return; }
    searchContact(search);
  };

  const onValueChange = (value:any) => {
    if (disabled) { return; }
    setState(STATE.render);
    if (!value) {
      onChange(0);
      return;
    }
    dispatch(getContactByIdAction(value.id as number));
    onChange(value.id as number);
  };

  const onClickEdit = () => {
    if (disabled) { return; }
    dispatch(setSelections({
      entity: ENTITIES.contacts,
      selections: [contactId],
    }));
    dispatch(openModal({ modal: MODALS.editContacts }));
  };

  const onClickAdd = () => {
    if (disabled) { return; }
    dispatch(setSelections({
      entity: ENTITIES.contacts,
      selections: [contactId],
    }));
    dispatch(openModal({
      modal: MODALS.addContact,
      payload: {
        onChange,
        reload: false,
      },
    }));
  };

  const getLabel = (option: ContactOption) => {
    const {
      companyName, firstName, lastName, address,
    } = option;

    if (companyName && (!firstName && !lastName)) {
      return `${companyName} (${address || ''})`;
    }

    const namePart = firstName && lastName ? `${firstName} ${lastName}` : '';
    return `${namePart} (${address || ''})`;
  };

  const getOptions = () => {
    if (!contactOptions) return [];
    return contactOptions.map((contact) => ({
      id: contact.id,
      label: getLabel(contact),
    })).sort((a, b) => a.label.localeCompare(b.label));
  };

  const renderSearch = () => (
    <FormControl
      id={id}
      variant="outlined"
      className={classes.formControl}
      key={`form-control-${name}`}>
      <Autocomplete
        fullWidth
        style={style}
        forcePopupIcon={false}
        onChange={(e, value) => onValueChange(value)}
        onInputChange={(e, value) => onInputChange(value)}
        options={getOptions()}
        getOptionLabel={(option: ContactItem) => option.label}
        getOptionSelected={(option, value) => option.id === value?.id}
        ListboxProps={{ id: `${id}-listbox` }}
        filterOptions={(x) => x}
        disabled={disabled}
        onBlur={() => setState(STATE.render)}
        renderInput={(params) => (
          <TextField
            {...params}
            id={id}
            inputProps={{
              ...params.inputProps,
              id: `${id}-search`,
            }}
            className={classes.root}
            autoFocus
            variant="outlined"
            label="Search contacts"
            placeholder="firstname, lastname or email"
            error={!!error || !!errors}
            margin={margin}
            onBlur={() => setState(STATE.render)} />
        )} />
    </FormControl>
  );

  const renderText = () => (
    <TextField
      id={`${id}-input`}
      disabled={disabled}
      variant="outlined"
      label={label}
      placeholder="firstname, lastname or email"
      error={!!error || !!errors}
      margin={margin}
      value={contactString}
      onClick={() => setState(STATE.search)}
      style={{ width: '100%' }} />
  );

  const renderComponent = () => (state === STATE.render ? renderText() : renderSearch());

  return (
    <div style={{ width: '100%' }}>
      <div>{renderComponent()}</div>
      <div className={classes.flexrow}>
        { !hideEdit && (
        <div
          className={disabled ? classes.link_disabled : classes.link}
          style={{ marginRight: '10px' }}
          onClick={() => onClickEdit()}>
          <i>{t(lang, 'menus.edit_contact')}</i>
        </div>
        )}
        { !hideAdd && (
          <div
            className={disabled ? classes.link_disabled : classes.link}
            onClick={() => onClickAdd()}>
            <i>{t(lang, 'menus.add_contact')}</i>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactSelector;
